import { RefObject, useContext, useEffect, useRef } from "react";
import { ScrollContainerContext } from "../components/templates/BottomNavLayout";

type Options = {
  /** 続きがあるか。無ければ監視しない */
  hasNextPage: boolean;
  /** 取得中か。取得中は監視を外して二重に呼ばないようにする */
  isFetchingNextPage: boolean;
  /** 番兵が見えたときに呼ぶ。useCallback で包んだものを渡す */
  onLoadMore: () => void;
};

/** 番兵がこの距離だけ手前に来た時点で「見えた」とみなし、下端に着く前に読み始める */
const PRELOAD_MARGIN = "0px 0px 200px 0px";

/*
 * 無限スクロール。返した ref を一覧の末尾の番兵要素に付けると、
 * 番兵が表示領域に入ったとき onLoadMore を呼ぶ。
 * 取得が終わって監視を張り直した時点で番兵がまだ見えていれば、すぐ次を呼ぶ
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: Options): RefObject<HTMLDivElement | null> {
  const sentinelRef = useRef<HTMLDivElement>(null);
  // 内側の要素でスクロールするレイアウトでは、その要素を基準にしないと、
  // 見えている範囲の外が切り取られて手前の余白が効かない。渡されなければ画面全体を基準にする
  const scrollContainerRef = useContext(ScrollContainerContext);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      {
        root: scrollContainerRef?.current ?? null,
        rootMargin: PRELOAD_MARGIN,
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore, scrollContainerRef]);

  return sentinelRef;
}
